import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { get } from 'lodash';
import { api } from '@/modules/common/api/index.ts';
import {
  ActivitiesPaginateResponse,
  GetCurrentUserActivitiesListResponse,
  MyActivitiesListV1SrActivitiesMyGetParams,
} from 'api';

export const CURRENT_USER_ACTIVITIES_QUERY_KEY = 'vestra/current-activities';
export const BATCH_ENTITIES_FIRST_ACTIVITIES_QUERY_KEY = 'vestra/batch-entities-first-activities';

export const useMyActivitiesListQuery = (
  payload: MyActivitiesListV1SrActivitiesMyGetParams,
  register_id: number,
  disabled = false,
  entity_id?: number,
) => {
  const { page, page_size, ...filters } = payload;
  return useInfiniteQuery<GetCurrentUserActivitiesListResponse>({
    queryKey: [
      CURRENT_USER_ACTIVITIES_QUERY_KEY,
      page_size,
      register_id,
      entity_id,
      ...Object.entries(filters).map(obj => obj[1]),
    ],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    refetchInterval: 1000 * 60, //1min
    queryFn: async ({ pageParam = 0 }) => {
      const response = await api.v1.myActivitiesListV1SrActivitiesMyGet(
        { ...payload, page: pageParam || page },
        entity_id ? { headers: { ['xEntity-ID']: entity_id } } : undefined,
      );
      return response.data;
    },
    keepPreviousData: true,
    retry: 2,
    getNextPageParam: ({ total, page, page_size }) => {
      const totalPage = Math.ceil(total / page_size);
      const hasMore = totalPage > page;
      if (hasMore) {
        return page + 1;
      }
    },
    enabled: !disabled,
  });
};

export const useBatchEntitiesFirstActivityListQuery = (
  payload: MyActivitiesListV1SrActivitiesMyGetParams,
  entitiesIds: number[],
  disabled = false,
) => {
  const { page, ...filters } = payload;
  return useQuery<Record<number, ActivitiesPaginateResponse[]>>({
    queryKey: [BATCH_ENTITIES_FIRST_ACTIVITIES_QUERY_KEY, JSON.stringify(filters), entitiesIds],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async ({ pageParam = 0 }) => {
      const promises = entitiesIds.map(async id =>
        api.v1.myActivitiesListV1SrActivitiesMyGet(
          { ...payload, page: pageParam || page },
          { headers: { ['xEntity-ID']: id, ['xRegister-ID']: null } },
        ),
      );

      const responses = await Promise.allSettled(promises);

      return responses.reduce(
        (acc, res, currentIndex) => ({
          ...acc,
          [entitiesIds[currentIndex]]: get(res, 'value.data.data', []),
        }),
        {},
      );
    },
    keepPreviousData: true,
    retry: 2,
    enabled: !disabled,
  });
};
